import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Eye, Info, Plus } from "lucide-react";
import { audienceKeyForDesignation } from "./emailRoleUtils";
import { templateDisplay } from "./templateLabel";

const TYPE_META = {
  invite: {
    title: "Invitation",
    blurb: "Sent when you first invite someone to the assessment.",
  },
  resend: {
    title: "Manual resend",
    blurb: "Sent when you resend from the invite list (follow-up reminder).",
  },
  auto_reminder: {
    title: "Automated reminders",
    blurb: "Sent by the reminder schedule unless a step assigns its own template.",
  },
};

function pickSharedTemplate(templates, type, stage = null) {
  const shared = templates.filter(
    (t) =>
      t.template_type === type &&
      t.role_group === "all" &&
      (type !== "auto_reminder" || Number(t.stage || 1) === Number(stage || 1))
  );
  return shared[0] || null;
}

function templatesForDesignationOverride(templates, typeKey, designationLabel, audiences) {
  const audienceKey = audienceKeyForDesignation(designationLabel, audiences);
  return templates.filter((t) => {
    if (t.template_type !== typeKey) return false;
    if (t.role_group === "all") return true;
    if (audienceKey && t.role_group === audienceKey) return true;
    return false;
  });
}

function pickAudienceTemplate(templates, type, audienceKey, stage = null) {
  return (
    templates.find(
      (t) =>
        t.template_type === type &&
        t.role_group === audienceKey &&
        (type !== "auto_reminder" || Number(t.stage || 1) === Number(stage || 1))
    ) || null
  );
}

const ROUTING_HELP_PANEL_WIDTH_PX = 352;

function RoutingSectionHelp({ label, children }) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const panelId = useId();
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);

  const updatePanelPos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const margin = 16;
    const maxLeft = window.innerWidth - margin - ROUTING_HELP_PANEL_WIDTH_PX;
    const left = Math.min(Math.max(margin, rect.left), Math.max(margin, maxLeft));
    setPanelPos({ top: rect.bottom + 6, left });
  }, []);

  const cancelClose = () => {
    clearTimeout(closeTimerRef.current);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  const show = () => {
    cancelClose();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    updatePanelPos();
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", updatePanelPos, true);
    window.addEventListener("resize", updatePanelPos);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", updatePanelPos, true);
      window.removeEventListener("resize", updatePanelPos);
    };
  }, [open, updatePanelPos]);

  useEffect(
    () => () => {
      cancelClose();
    },
    []
  );

  const panel =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="region"
            aria-label={label}
            className="fixed z-[9999] box-border max-h-[min(70vh,24rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-xs leading-relaxed text-slate-600 shadow-lg"
            style={{
              top: panelPos.top,
              left: panelPos.left,
              width: `min(${ROUTING_HELP_PANEL_WIDTH_PX}px, calc(100vw - 2rem))`,
            }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {children}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/40 focus-visible:ring-offset-1"
        aria-expanded={open}
        aria-controls={panelId}
        aria-describedby={open ? panelId : undefined}
        aria-label={label}
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next) updatePanelPos();
            return next;
          });
        }}
        onMouseEnter={() => {
          show();
          updatePanelPos();
        }}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          show();
          updatePanelPos();
        }}
      >
        <Info size={16} strokeWidth={2} aria-hidden />
      </button>
      {panel}
    </>
  );
}

function TemplatePickRow({
  label,
  hint,
  template,
  audienceLabel,
  onEdit,
  onPreview,
  onAdd,
  placeholder = "No template selected",
}) {
  const display = template ? templateDisplay(template, audienceLabel) : null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {hint ? <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p> : null}
        <p className="text-xs text-slate-600 mt-1 truncate">
          {display ? display.title : placeholder}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {template ? (
          <>
            <button
              type="button"
              className="p-1.5 rounded-md text-[#004f73] hover:bg-[#004f73]/10"
              onClick={() => onPreview(template.id, audienceLabel || label)}
              aria-label={`Preview ${label}`}
            >
              <Eye size={14} />
            </button>
            <button
              type="button"
              className="px-2.5 py-1.5 text-xs font-semibold text-[#004f73] border border-[#004f73]/30 rounded-md hover:bg-[#004f73]/5"
              onClick={() => onEdit(template.id)}
            >
              Edit
            </button>
          </>
        ) : onAdd ? (
          <button
            type="button"
            className="p-2 rounded-md text-[#004f73] border border-[#004f73]/30 hover:bg-[#004f73]/10"
            onClick={onAdd}
            aria-label={`Create template for ${label}`}
            title="Create template"
          >
            <Plus size={16} strokeWidth={2.5} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function TypeRoutingCard({
  typeKey,
  routing,
  templates,
  audiences,
  designations,
  allowPerRole,
  onTogglePerRole,
  onOverrideChange,
  onEdit,
  onPreview,
  onCreateMissing,
  isClassicWorkspace,
  saving,
}) {
  const meta = TYPE_META[typeKey];
  const cfg = routing[typeKey] || { per_role: false, shared_template_id: null, designation_overrides: {} };
  const [overridesOpen, setOverridesOpen] = useState(false);

  const sharedInviteResend = useMemo(() => {
    if (cfg.shared_template_id) {
      return templates.find((t) => t.id === cfg.shared_template_id) || null;
    }
    return pickSharedTemplate(templates, typeKey);
  }, [cfg.shared_template_id, templates, typeKey]);

  const reminderStages = useMemo(() => {
    if (typeKey !== "auto_reminder") return [];
    return [1, 2, 3].map((stage) => ({
      stage,
      template: pickSharedTemplate(templates, typeKey, stage),
    }));
  }, [templates, typeKey]);

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/40 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-bold text-slate-900">{meta.title}</h4>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{meta.blurb}</p>
        </div>
        {allowPerRole ? (
          <label className="flex items-center gap-2 shrink-0 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-[#004f73]"
              checked={!!cfg.per_role}
              disabled={saving}
              onChange={(e) => onTogglePerRole(typeKey, e.target.checked)}
            />
            Different email per role
          </label>
        ) : null}
      </div>

      {!cfg.per_role || !allowPerRole ? (
        <>
          {typeKey === "auto_reminder" ? (
            <div className="space-y-2">
              {reminderStages.map(({ stage, template }) => (
                <TemplatePickRow
                  key={stage}
                  label={`Stage ${stage}`}
                  template={template}
                  audienceLabel="All roles"
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onAdd={
                    onCreateMissing
                      ? () =>
                          onCreateMissing({
                            template_type: typeKey,
                            role_group: "all",
                            stage,
                          })
                      : undefined
                  }
                  placeholder={`No shared stage ${stage} template yet`}
                />
              ))}
            </div>
          ) : (
            <TemplatePickRow
              label="All roles"
              hint={
                allowPerRole
                  ? "Executive, Leadership Team, Manager, Contributor, and Other receive this email unless you add an exception below."
                  : "One email for Executive, Leadership Team, Manager, Contributor, and Other on this workspace."
              }
              template={sharedInviteResend}
              audienceLabel="All roles"
              onEdit={onEdit}
              onPreview={onPreview}
              onAdd={
                onCreateMissing && !sharedInviteResend
                  ? () =>
                      onCreateMissing({
                        template_type: typeKey,
                        role_group: "all",
                      })
                  : undefined
              }
            />
          )}

          {allowPerRole ? (
          <div className="border-t border-slate-200 pt-2">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 text-xs font-medium text-slate-600 hover:text-slate-900 py-1"
              onClick={() => setOverridesOpen((v) => !v)}
              aria-expanded={overridesOpen}
            >
              <span>Role-specific exceptions (optional)</span>
              <ChevronDown
                size={14}
                className={`shrink-0 transition-transform ${overridesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {overridesOpen ? (
              <div className="mt-2 space-y-2">
                {designations.map((label) => {
                  const overrideId = cfg.designation_overrides?.[label] || "";
                  const tpl = overrideId ? templates.find((t) => t.id === overrideId) : null;
                  return (
                    <div key={label} className="flex items-center gap-2">
                      <span className="w-32 shrink-0 text-xs text-slate-600 truncate" title={label}>
                        {label}
                      </span>
                      <select
                        className="flex-1 min-w-0 border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                        value={overrideId}
                        disabled={saving}
                        onChange={(e) => onOverrideChange(typeKey, label, e.target.value || null)}
                      >
                        <option value="">Use shared email</option>
                        {templatesForDesignationOverride(
                          templates,
                          typeKey,
                          label,
                          audiences
                        ).map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      {tpl ? (
                        <button
                          type="button"
                          className="shrink-0 p-1.5 rounded-md text-[#004f73] hover:bg-[#004f73]/10"
                          onClick={() => onEdit(tpl.id)}
                          aria-label={`Edit override for ${label}`}
                        >
                          Edit
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
          ) : null}
        </>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-500">
            Each recipient audience uses its own template. Map invite roles to audiences under Manage
            audiences if needed.
          </p>
          {(isClassicWorkspace
            ? audiences.map((a) => ({ key: a.key, label: a.label, audience: a }))
            : designations.map((label) => {
                const key = audienceKeyForDesignation(label, audiences);
                const audience = audiences.find((a) => a.key === key);
                return { key, label, audience };
              })
          ).map(({ key, label, audience }) => {
            if (!key) return null;
            if (typeKey === "auto_reminder") {
              return (
                <div key={key} className="space-y-2">
                  <p className="text-xs font-semibold text-slate-700">{label}</p>
                  {[1, 2, 3].map((stage) => {
                    const tpl = pickAudienceTemplate(templates, typeKey, key, stage);
                    return (
                      <TemplatePickRow
                        key={`${key}-${stage}`}
                        label={`Stage ${stage}`}
                        template={tpl}
                        audienceLabel={label}
                        onEdit={onEdit}
                        onPreview={onPreview}
                        onAdd={
                          onCreateMissing && !tpl
                            ? () =>
                                onCreateMissing({
                                  template_type: typeKey,
                                  role_group: key,
                                  stage,
                                })
                            : undefined
                        }
                        placeholder={`No stage ${stage} template for ${label}`}
                      />
                    );
                  })}
                </div>
              );
            }
            const tpl = pickAudienceTemplate(templates, typeKey, key);
            const matchHint =
              !isClassicWorkspace && audience
                ? `Audience: ${audience.label}`
                : (audience?.match_designations || []).length
                  ? `Roles: ${audience.match_designations.join(", ")}`
                  : null;
            return (
              <TemplatePickRow
                key={key}
                label={label}
                hint={matchHint}
                template={tpl}
                audienceLabel={label}
                onEdit={onEdit}
                onPreview={onPreview}
                onAdd={
                  onCreateMissing && !tpl
                    ? () =>
                        onCreateMissing({
                          template_type: typeKey,
                          role_group: key,
                        })
                    : undefined
                }
                placeholder={`No ${meta.title.toLowerCase()} for ${label}`}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function EmailTemplateRoutingPanel({
  routing,
  templates,
  audiences,
  designations,
  allowPerRole = true,
  isClassicWorkspace = false,
  saving,
  onSaveRouting,
  onEditTemplate,
  onPreviewTemplate,
  onGenerateAllDrafts,
  generatingAllDrafts,
  onCreateMissing,
}) {
  const updateRouting = async (next) => {
    await onSaveRouting(next);
  };

  const handleTogglePerRole = async (typeKey, perRole) => {
    const next = {
      ...routing,
      [typeKey]: {
        ...(routing[typeKey] || {}),
        per_role: perRole,
      },
    };
    await updateRouting(next);
    if (perRole && !isClassicWorkspace && onGenerateAllDrafts && typeKey === "invite") {
      await onGenerateAllDrafts({ perRole: true });
    }
  };

  const handleOverrideChange = async (typeKey, designation, templateId) => {
    const prev = routing[typeKey]?.designation_overrides || {};
    const designation_overrides = { ...prev };
    if (templateId) designation_overrides[designation] = templateId;
    else delete designation_overrides[designation];
    const next = {
      ...routing,
      [typeKey]: {
        ...(routing[typeKey] || {}),
        designation_overrides,
      },
    };
    await updateRouting(next);
  };

  const routingHelpLabel = "How email routing works";
  const routingSummaryLine = isClassicWorkspace
    ? "Separate CEO/Leadership vs PE/Board invite copy by default."
    : "Shared invite, resend, and reminders for all roles by default.";

  const routingHelpDetails =
    allowPerRole || (!isClassicWorkspace && onGenerateAllDrafts) ? (
      <>
        <p className="text-slate-600">
          {isClassicWorkspace
            ? "5X classic uses separate invitation and resend copy for CEO & Leadership Team vs PE & Board by default."
            : "Tenant workspaces use one shared invitation, resend, and reminder for every role (Executive through Other) unless you enable per-role email."}
        </p>
        {allowPerRole ? (
          <p className="mt-2 text-slate-600">
            <span className="font-semibold text-slate-800">Per-role mode</span> (checkbox on each block
            below):{" "}
            {isClassicWorkspace ? (
              <>
                <span className="font-semibold">On</span> = keep the two 5X audience templates.{" "}
                <span className="font-semibold">Off</span> = optional single &ldquo;All roles&rdquo; email
                instead (plus designation exceptions).
              </>
            ) : (
              <>
                <span className="font-semibold">Off</span> = shared email for all roles.{" "}
                <span className="font-semibold">On</span> = separate template per audience (
                {audiences.map((a) => a.label).join(", ")}).
              </>
            )}
          </p>
        ) : null}
        {!isClassicWorkspace && onGenerateAllDrafts ? (
          <p className={allowPerRole ? "mt-2 text-slate-500" : "text-slate-500"}>
            Turn on <span className="font-semibold">Different email per role</span> below, or use
            &ldquo;Generate AI email drafts for all roles&rdquo; to fill missing invite, resend, and reminder
            drafts for every audience (Executive through Other).
          </p>
        ) : null}
      </>
    ) : null;

  return (
    <div className="space-y-4 mb-8">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold leading-6 text-slate-900">Who gets which email</h3>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
            <p
              className="min-w-0 flex-1 truncate text-xs leading-5 text-slate-500"
              title={routingSummaryLine}
            >
              {routingSummaryLine}
            </p>
            {routingHelpDetails ? (
              <RoutingSectionHelp label={routingHelpLabel}>{routingHelpDetails}</RoutingSectionHelp>
            ) : null}
          </div>
        </div>
        {!isClassicWorkspace && onGenerateAllDrafts ? (
          <button
            type="button"
            className="shrink-0 rounded-lg border border-[#004f73]/30 px-3 py-2 text-xs font-semibold leading-5 text-[#004f73] hover:bg-[#004f73]/5 disabled:opacity-50"
            disabled={generatingAllDrafts || saving}
            onClick={() => onGenerateAllDrafts()}
          >
            {generatingAllDrafts
              ? "Generating AI drafts…"
              : "Generate AI email drafts for all roles"}
          </button>
        ) : null}
      </div>
      {(["invite", "resend", "auto_reminder"]).map((typeKey) => (
        <TypeRoutingCard
          key={typeKey}
          typeKey={typeKey}
          routing={routing}
          templates={templates}
          audiences={audiences}
          designations={designations}
          allowPerRole={allowPerRole}
          saving={saving}
          onTogglePerRole={handleTogglePerRole}
          onOverrideChange={handleOverrideChange}
          isClassicWorkspace={isClassicWorkspace}
          onEdit={onEditTemplate}
          onPreview={onPreviewTemplate}
          onCreateMissing={onCreateMissing}
        />
      ))}
    </div>
  );
}
